import { Form, Select, Spin } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import _ from 'lodash';
import React from 'react';
const { Option } = Select;

interface Provider {
  id: string;
  name: string;
}

interface Props {
  form: WrappedFormUtils;
  providers: Provider[];
  loading?: boolean;
  // The providers are searched on the server: `providers` is the result for `search`, and
  // typing reports the new text through `onSearch`.
  search?: string;
  onSearch?: (search: string) => void;
  // The provider named by a providerId in the URL, which may not be in `providers`.
  prefilledProvider?: Provider;
  // `providers` is a capped page, so there may be more providers than it shows.
  hasMore?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;

interface State {
  // The provider last picked from the list, kept so its name still shows once a search
  // that found it is cleared and it is no longer in `providers`.
  pickedProvider?: Provider;
}

export default class SelectProviderField extends React.Component<Props, State> {
  state: State = {};

  handleSearch = _.debounce((search: string) => {
    if (this.props.onSearch) this.props.onSearch(search.trim());
  }, SEARCH_DEBOUNCE_MS);

  // Picking a provider or leaving the field clears the input, so go back to the first page
  // too - otherwise the next open shows a stale search the input no longer shows.
  resetSearch = (): void => {
    this.handleSearch.cancel();
    if (this.props.search && this.props.onSearch) this.props.onSearch('');
  };

  handleSelect = (value: string): void => {
    const pickedProvider = this.props.providers.find(provider => provider.id === value);
    if (pickedProvider) this.setState({ pickedProvider });
    this.resetSearch();
  };

  componentWillUnmount(): void {
    this.handleSearch.cancel();
  }

  // The provider list is loaded lazily, so it usually arrives after this field has
  // registered and antd v3 has already taken its initialValue. Apply the single-provider
  // default when the list lands instead - unless the user (or a providerId in the URL)
  // has already chosen one, or the list is a search result rather than every provider.
  componentDidUpdate(prevProps: Props): void {
    const { form, providers, search } = this.props;

    if (prevProps.providers === providers) return;
    if (search) return;
    if (providers.length !== 1) return;
    if (form.getFieldValue('sendingProvider')) return;

    form.setFieldsValue({ sendingProvider: providers[0].id });
  }

  render(): JSX.Element {
    const { form, providers, loading, search, prefilledProvider, hasMore } = this.props;
    const { getFieldDecorator } = form;

    // The selected provider may not be in this page of `providers` - a providerId from the
    // URL, or a search result picked before the search was cleared - so it gets an option of
    // its own to show its name rather than its id.
    const selectedId = form.getFieldValue('sendingProvider');
    const selected = [this.state.pickedProvider, prefilledProvider].find(
      provider => provider && provider.id === selectedId
    );
    const shown =
      selected && !providers.some(provider => provider.id === selected.id) ? [selected, ...providers] : providers;

    const options = {
      ...(!search && providers.length === 1 && { initialValue: providers[0].id }),
      ...(true && { rules: [{ required: true, message: 'Required' }] }),
    };

    return (
      <Form.Item label="Who is the sender?">
        {getFieldDecorator(
          'sendingProvider',
          options
        )(
          <Select
            showSearch
            loading={loading}
            placeholder="Sending Provider"
            filterOption={false}
            onSearch={this.handleSearch}
            onSelect={this.handleSelect}
            onBlur={this.resetSearch}
            notFoundContent={loading ? <Spin size="small" /> : 'No providers found'}
            getPopupContainer={trigger => trigger.parentElement || document.body}
            dropdownRender={menu => (
              <div>
                {menu}
                {hasMore && (
                  <div className="provider-search-hint" style={{ padding: '4px 12px', color: 'rgba(0, 0, 0, 0.45)' }}>
                    {search ? 'Keep typing to narrow the results' : 'Type a name to search all providers'}
                  </div>
                )}
              </div>
            )}
          >
            {shown.map(provider => (
              <Option value={provider.id} key={provider.id}>
                {provider.name}
              </Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  }
}
